import React, { useEffect, useState } from "react";
import {
  FiRefreshCw,
  FiCalendar,
} from "react-icons/fi";

import { getDailyPayments } from "./CashAndDisbursementsService";

const Treasury = () => {
  // =========================================================
  // State
  // =========================================================

  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const [representatives, setRepresentatives] =
    useState([]);

  const [summary, setSummary] = useState({
    totalCashAmount: 0,
    totalInstaPayAmount: 0,
    totalVodafoneCashAmount: 0,
    grandTotalPaid: 0,
    totalRepresentatives: 0,
  });

  const [loading, setLoading] = useState(false);

  // =========================================================
  // Format Number
  // =========================================================

  const formatNumber = (value) => {
    return Number(value || 0).toLocaleString("ar-EG");
  };

  // =========================================================
  // Load Daily Payments
  // =========================================================

  const loadDailyPayments = async () => {
    try {
      setLoading(true);

      const response = await getDailyPayments(
        selectedDate
      );

      const result = response?.data || response;

      setRepresentatives(
        Array.isArray(result?.representatives)
          ? result.representatives
          : []
      );

      setSummary({
        totalCashAmount: Number(
          result?.totalCashAmount || 0
        ),

        totalInstaPayAmount: Number(
          result?.totalInstaPayAmount || 0
        ),

        totalVodafoneCashAmount: Number(
          result?.totalVodafoneCashAmount || 0
        ),

        grandTotalPaid: Number(
          result?.grandTotalPaid || 0
        ),

        totalRepresentatives: Number(
          result?.totalRepresentatives || 0
        ),
      });
    } catch (error) {
      console.error(
        "GET DAILY PAYMENTS ERROR:",
        error
      );

      setRepresentatives([]);

      setSummary({
        totalCashAmount: 0,
        totalInstaPayAmount: 0,
        totalVodafoneCashAmount: 0,
        grandTotalPaid: 0,
        totalRepresentatives: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // Load when date changes
  // =========================================================

  useEffect(() => {
    loadDailyPayments();
  }, [selectedDate]);

  // =========================================================
  // Status Class
  // =========================================================

  const getStatusClass = (status) => {
    if (!status) {
      return "treasury-status treasury-status-default";
    }

    if (
      status.includes("جزئي")
    ) {
      return "treasury-status treasury-status-partial";
    }

    if (
      status.includes("غير")
    ) {
      return "treasury-status treasury-status-unclosed";
    }

    if (
      status.includes("مقفل") ||
      status.includes("كامل")
    ) {
      return "treasury-status treasury-status-complete";
    }

    return "treasury-status treasury-status-default";
  };

  // =========================================================
  // Render
  // =========================================================

  return (
    <section className="cash-card treasury-card">
      {/* =====================================================
          Header
      ====================================================== */}

      <div className="cash-card-header">
        <div>
          <h2 className="cash-card-title">
            حركة الخزنة
          </h2>

          <p className="cash-card-subtitle">
            متابعة مدفوعات المناديب اليومية
          </p>
        </div>

        <button
          type="button"
          className="cash-refresh-button"
          onClick={loadDailyPayments}
          disabled={loading}
        >
          <FiRefreshCw
            size={15}
            className={
              loading
                ? "cash-spin"
                : ""
            }
          />

          تحديث
        </button>
      </div>

      {/* =====================================================
          Date
      ====================================================== */}

      <div className="treasury-date-wrapper">
        <label>
          <FiCalendar size={15} />

          التاريخ
        </label>

        <input
          type="date"
          value={selectedDate}
          onChange={(event) =>
            setSelectedDate(
              event.target.value
            )
          }
        />
      </div>

      {/* =====================================================
          Summary
      ====================================================== */}

      <div className="treasury-summary">
        <div className="treasury-summary-item">
          <span>الكاش</span>

          <strong>
            {formatNumber(
              summary.totalCashAmount
            )}
          </strong>
        </div>

        <div className="treasury-summary-item">
          <span>InstaPay</span>

          <strong>
            {formatNumber(
              summary.totalInstaPayAmount
            )}
          </strong>
        </div>

        <div className="treasury-summary-item">
          <span>Vodafone Cash</span>

          <strong>
            {formatNumber(
              summary.totalVodafoneCashAmount
            )}
          </strong>
        </div>

        <div className="treasury-summary-item">
          <span>الإجمالي</span>

          <strong>
            {formatNumber(
              summary.grandTotalPaid
            )}
          </strong>
        </div>
      </div>

      {/* =====================================================
          Table
      ====================================================== */}

      <div className="cash-table-wrapper">
        <table className="cash-table treasury-table">
          <thead>
            <tr>
              <th>م</th>

              <th>
                المندوب
              </th>

              <th>
                الكاش
              </th>

              <th>
                InstaPay
              </th>

              <th>
                Vodafone Cash
              </th>

              <th>
                الإجمالي
              </th>

              <th>
                الحالة
              </th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan="7"
                  className="cash-table-message"
                >
                  جاري تحميل حركة الخزنة...
                </td>
              </tr>
            ) : representatives.length === 0 ? (
              <tr>
                <td
                  colSpan="7"
                  className="cash-table-message"
                >
                  لا توجد حركة خزنة لهذا اليوم
                </td>
              </tr>
            ) : (
              <>
                {representatives.map(
                  (representative, index) => (
                    <tr
                      key={
                        representative.representativeId ||
                        index
                      }
                    >
                      {/* م */}

                      <td>
                        {index + 1}
                      </td>

                      {/* المندوب */}

                      <td className="treasury-representative">
                        <div className="treasury-representative-name">
                          {representative.representativeName ||
                            "-"}
                        </div>

                        <div className="treasury-representative-phone">
                          {representative.phoneNumber ||
                            "-"}
                        </div>

                        <div className="treasury-representative-governorate">
                          {representative.governorateName ||
                            "-"}
                        </div>
                      </td>

                      {/* Cash */}

                      <td className="treasury-money">
                        {formatNumber(
                          representative.cashAmount
                        )}
                      </td>

                      {/* InstaPay */}

                      <td className="treasury-money">
                        {formatNumber(
                          representative.instaPayAmount
                        )}
                      </td>

                      {/* Vodafone */}

                      <td className="treasury-money">
                        {formatNumber(
                          representative.vodafoneCashAmount
                        )}
                      </td>

                      {/* Total */}

                      <td className="treasury-total">
                        {formatNumber(
                          representative.totalPaid
                        )}
                      </td>

                      {/* Status */}

                      <td>
                        <span
                          className={getStatusClass(
                            representative.settlementStatus
                          )}
                        >
                          {representative.settlementStatus ||
                            "-"}
                        </span>
                      </td>
                    </tr>
                  )
                )}

                {/* =================================================
                    TOTAL ROW
                ================================================== */}

                <tr className="treasury-total-row">
                  <td
                    colSpan="2"
                  >
                    الإجمالي
                  </td>

                  <td>
                    {formatNumber(
                      summary.totalCashAmount
                    )}
                  </td>

                  <td>
                    {formatNumber(
                      summary.totalInstaPayAmount
                    )}
                  </td>

                  <td>
                    {formatNumber(
                      summary.totalVodafoneCashAmount
                    )}
                  </td>

                  <td>
                    {formatNumber(
                      summary.grandTotalPaid
                    )}
                  </td>

                  <td>
                    -
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default Treasury;